/**
 * TrialCountdownPanel — warm conversion view for approved trials.
 *
 * Buckets: urgent (0-2d) · warm (3-5d) · early (6+d) · no_clock (approved,
 * never signed in). One-click extend + resend-magic-link inline per row.
 *
 * Reads: GET /api/admin/trials/countdown
 * Writes:
 *   POST /api/admin/trials/{id}/extend            { days, reason }
 *   POST /api/admin/trials/{id}/resend-magic-link
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Clock, Flame, AlertTriangle, Mail, MailCheck, Plus, X,
  CheckCircle2, Sparkles, Send,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BUCKET_META = {
  urgent: {
    label: "Urgent",
    sub: "0–2 days remaining · engage now",
    Icon: Flame,
    accent: "bg-rose-50 border-rose-300 text-rose-800",
    pill: "bg-rose-600 text-white",
  },
  warm: {
    label: "Warm",
    sub: "3–5 days · soft outreach window",
    Icon: AlertTriangle,
    accent: "bg-amber-50 border-amber-300 text-amber-900",
    pill: "bg-amber-500 text-white",
  },
  early: {
    label: "Early",
    sub: "6+ days · monitor only",
    Icon: Clock,
    accent: "ek-surface-2 ek-line ek-text",
    pill: "bg-zinc-700 text-white",
  },
  no_clock: {
    label: "Awaiting sign-in",
    sub: "Approved but the user hasn’t accepted the magic-link yet",
    Icon: Mail,
    accent: "bg-indigo-50 border-indigo-300 text-indigo-900",
    pill: "bg-[#7737F6] text-white",
  },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
};

function ExtendModal({ trial, onClose, onConfirm, submitting }) {
  const [days, setDays] = useState(7);
  const [reason, setReason] = useState("");
  return (
    <div
      className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4"
      data-testid="trial-extend-modal"
    >
      <div className="ek-surface max-w-lg w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b ek-line">
          <h3 className="ek-heading text-lg">Extend {trial.full_name}&apos;s trial</h3>
          <button
            type="button"
            onClick={onClose}
            className="ek-text-muted hover:text-black"
            data-testid="trial-extend-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Days to add (1–30)
            <input
              type="number"
              min={1}
              max={30}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(30, parseInt(e.target.value || "0", 10) || 1)))}
              className="mt-2 w-full border ek-line px-3 py-2 text-[14px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="trial-extend-days"
            />
          </label>
          <label className="block text-[12px] tracking-[0.14em] uppercase ek-text-muted">
            Reason (audit log, optional)
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="mt-2 w-full border ek-line px-3 py-2 text-[13px] focus:outline-none focus:border-[#7737F6] normal-case tracking-normal ek-text"
              data-testid="trial-extend-reason"
              placeholder="e.g. Final demo booked — need them live through next week"
            />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t ek-line ek-surface-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[12px] tracking-[0.14em] uppercase ek-text-soft hover:text-black"
            data-testid="trial-extend-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ days, reason: reason.trim() })}
            disabled={submitting}
            className="ek-btn bg-[#7737F6] text-white text-[12px] tracking-[0.14em] uppercase px-5 py-2 disabled:opacity-60"
            data-testid="trial-extend-confirm"
          >
            {submitting ? "Extending…" : `Add ${days} day${days === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CountdownPill({ days, signedIn }) {
  if (!signedIn) {
    return (
      <span className="text-[11px] tracking-[0.12em] uppercase text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5">
        Not signed in
      </span>
    );
  }
  if (days === null || days === undefined) {
    return <span className="text-[11px] ek-text-muted">—</span>;
  }
  if (days <= 0) {
    return (
      <span className="text-[11px] tracking-[0.12em] uppercase text-rose-700 bg-rose-50 border border-rose-300 px-2 py-0.5">
        Expired
      </span>
    );
  }
  const cls =
    days <= 2 ? "text-rose-700 bg-rose-50 border-rose-300" :
    days <= 5 ? "text-amber-800 bg-amber-50 border-amber-300" :
    "ek-text-soft ek-surface-2 ek-line";
  return (
    <span className={`text-[11px] tracking-[0.12em] uppercase border px-2 py-0.5 ${cls}`}>
      {days} day{days === 1 ? "" : "s"} left
    </span>
  );
}

function Row({ trial, bucket, onExtend, onResend, busyId }) {
  const isBusy = busyId === trial.id;
  return (
    <tr className="border-t ek-line align-top" data-testid={`countdown-row-${trial.id}`}>
      <td className="px-3 py-3 text-[12.5px]">
        <div className="font-medium">{trial.full_name}</div>
        <div className="ek-text-muted inline-flex items-center gap-1">
          <Mail size={11} /> {trial.business_email}
        </div>
        <div className="ek-text-muted">{trial.job_title}</div>
      </td>
      <td className="px-3 py-3 text-[12.5px]">
        <div className="font-medium">{trial.company}</div>
        <div className="ek-text-muted">{trial.sector} · {trial.country}</div>
      </td>
      <td className="px-3 py-3">
        <CountdownPill
          days={trial.days_remaining}
          signedIn={!!trial.first_sign_in_at}
        />
        <div className="text-[11px] ek-text-muted mt-1">
          Expires {fmtDate(trial.expires_at)}
        </div>
        {trial.extension_count > 0 && (
          <div className="text-[10.5px] ek-text-muted mt-0.5">
            Extended ×{trial.extension_count}
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-[11.5px] ek-text-muted">
        <div>Approved {fmtDate(trial.approved_at)}</div>
        <div>
          Magic link {trial.magic_link_sent_at ? "sent" : "—"}
          {trial.magic_link_sent_at && ` ${fmtDate(trial.magic_link_sent_at)}`}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-2 justify-end">
          {bucket === "no_clock" && (
            <button
              type="button"
              onClick={() => onResend(trial)}
              disabled={isBusy}
              className="border border-[#7737F6] text-[#7737F6] text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:bg-[#7737F6] hover:text-white disabled:opacity-60"
              data-testid={`countdown-resend-${trial.id}`}
              title="Re-send the Auth0 passwordless magic-link"
            >
              <MailCheck size={12} /> Resend link
            </button>
          )}
          <button
            type="button"
            onClick={() => onExtend(trial)}
            disabled={isBusy}
            className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black disabled:opacity-60"
            data-testid={`countdown-extend-${trial.id}`}
          >
            <Plus size={12} /> Extend
          </button>
          <a
            href={`mailto:${trial.business_email}?subject=Following up on your EKSELENS360 trial`}
            className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black"
            data-testid={`countdown-mailto-${trial.id}`}
          >
            <Mail size={12} /> Email
          </a>
        </div>
      </td>
    </tr>
  );
}

function Bucket({ name, rows, onExtend, onResend, busyId }) {
  const meta = BUCKET_META[name];
  const { Icon } = meta;
  return (
    <section
      className={`border ${meta.accent} mb-6`}
      data-testid={`countdown-bucket-${name}`}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-current/20">
        <div className="flex items-center gap-3">
          <Icon size={18} />
          <div>
            <div className="ek-heading text-base inline-flex items-center gap-2">
              {meta.label}
              <span
                className={`inline-flex items-center justify-center min-w-[1.6rem] h-[1.4rem] px-2 text-[11px] tracking-[0.12em] uppercase ${meta.pill}`}
                data-testid={`countdown-bucket-count-${name}`}
              >
                {rows.length}
              </span>
            </div>
            <div className="text-[11.5px] text-current/80 mt-0.5">{meta.sub}</div>
          </div>
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-[12.5px] ek-text-muted ek-surface">
          No trials in this bucket.
        </div>
      ) : (
        <div className="ek-surface overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead className="ek-surface-2 ek-text-muted text-left text-[11px] tracking-[0.12em] uppercase">
              <tr>
                <th className="px-3 py-2">Person</th>
                <th className="px-3 py-2">Company</th>
                <th className="px-3 py-2">Countdown</th>
                <th className="px-3 py-2">Trail</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <Row
                  key={t.id}
                  trial={t}
                  bucket={name}
                  onExtend={onExtend}
                  onResend={onResend}
                  busyId={busyId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ToolTrialPill({ row }) {
  if (row.expired) {
    return (
      <span className="text-[11px] tracking-[0.12em] uppercase text-rose-700 bg-rose-50 border border-rose-300 px-2 py-0.5">
        Expired
      </span>
    );
  }
  const d = row.days_remaining;
  const cls =
    d < 1 ? "text-rose-700 bg-rose-50 border-rose-300" :
    d <= 2 ? "text-amber-800 bg-amber-50 border-amber-300" :
    "ek-text-soft ek-surface-2 ek-line";
  const label = d < 1 ? `${row.hours_remaining}h left` : `${d} day${d === 1 ? "" : "s"} left`;
  return (
    <span className={`text-[11px] tracking-[0.12em] uppercase border px-2 py-0.5 ${cls}`}>
      {label}
    </span>
  );
}

function ToolTrialsExpiring() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/trials/expiring-soon");
      setData(r.data);
    } catch (err) {
      if (err?.response?.status !== 403) toast.error("Couldn't load tool-trial prospects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep the "expiring soon" countdown current without operator intervention.
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const runNudges = async () => {
    setNudging(true);
    try {
      const r = await api.post("/admin/trials/prospect-nudge/run");
      const n = r.data?.counts?.nudged ?? 0;
      toast.success(n > 0 ? `Sent ${n} nudge${n === 1 ? "" : "s"}` : "No prospects were due a nudge");
      await load();
    } catch {
      toast.error("Couldn't send nudges.");
    } finally {
      setNudging(false);
    }
  };

  const items = data?.items || [];
  if (loading && !data) {
    return (
      <div className="border ek-line px-4 py-6 mb-8 text-[12.5px] ek-text-muted" data-testid="tool-trials-loading">
        Loading tool-trial prospects…
      </div>
    );
  }

  return (
    <section className="border border-[#E0CFFB] bg-[#F8F4FE] mb-8" data-testid="tool-trials-expiring">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#E0CFFB]">
        <div className="flex items-center gap-3">
          <Sparkles size={18} className="text-[#7737F6]" />
          <div>
            <div className="ek-heading text-base inline-flex items-center gap-2">
              Tool-trial prospects
              <span className="inline-flex items-center justify-center min-w-[1.6rem] h-[1.4rem] px-2 text-[11px] tracking-[0.12em] uppercase bg-[#7737F6] text-white" data-testid="tool-trials-count">
                {items.length}
              </span>
            </div>
            <div className="text-[11.5px] text-[#6B4BA3] mt-0.5">
              Invited <code>/tools</code> evaluations expiring soon · auto-nudged at ~1 day left
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={runNudges}
            disabled={nudging}
            className="border border-[#7737F6] text-[#7737F6] text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-[#7737F6] hover:text-white disabled:opacity-60"
            data-testid="tool-trials-nudge-now"
          >
            <Send size={12} /> {nudging ? "Sending…" : "Send nudges now"}
          </button>
          <button
            type="button"
            onClick={load}
            className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1.5 hover:border-black"
            data-testid="tool-trials-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </header>
      {items.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12.5px] ek-text-muted ek-surface" data-testid="tool-trials-empty">
          No tool-trial prospects expiring soon. Invite one from <strong>Users</strong> with a trial / campaign role.
        </div>
      ) : (
        <div className="ek-surface overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead className="ek-surface-2 ek-text-muted text-left text-[11px] tracking-[0.12em] uppercase">
              <tr>
                <th className="px-3 py-2">Prospect</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Countdown</th>
                <th className="px-3 py-2">Nudge</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.email} className="border-t ek-line align-top" data-testid={`tool-trial-row-${row.email}`}>
                  <td className="px-3 py-3 text-[12.5px]">
                    <div className="font-medium inline-flex items-center gap-1">
                      <Mail size={11} /> {row.email}
                    </div>
                    {row.name && <div className="ek-text-muted">{row.name}</div>}
                    <div className="text-[11px] ek-text-muted mt-0.5">
                      {row.ever_signed_in ? "Has signed in" : "Not signed in yet"}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-[12px] ek-text-muted capitalize">
                    {row.role === "campaign_user" ? "Campaign" : "Trial"}
                    {row.working_days_granted ? ` · ${row.working_days_granted}wd` : ""}
                  </td>
                  <td className="px-3 py-3">
                    <ToolTrialPill row={row} />
                    <div className="text-[11px] ek-text-muted mt-1">Ends {fmtDate(row.expires_at)}</div>
                  </td>
                  <td className="px-3 py-3 text-[11.5px]">
                    {row.nudged ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`tool-trial-nudged-${row.email}`}>
                        <MailCheck size={12} /> Sent
                      </span>
                    ) : (
                      <span className="ek-text-muted">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <a
                      href={`mailto:${row.email}?subject=Your EKSELENS360 evaluation`}
                      className="border ek-line ek-text-soft text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 inline-flex items-center gap-1 hover:border-black"
                      data-testid={`tool-trial-mailto-${row.email}`}
                    >
                      <Mail size={12} /> Email
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function TrialCountdownPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extending, setExtending] = useState(null); // trial obj
  const [busyId, setBusyId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/trials/countdown");
      setData(r.data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) toast.error("You don't have access to the trial countdown.");
      else toast.error("Couldn't load the trial countdown.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (trial) => {
    setBusyId(trial.id);
    try {
      const r = await api.post(`/admin/trials/${trial.id}/resend-magic-link`);
      const sent = r.data?.send_result?.sent;
      if (sent) {
        toast.success(`Magic link re-sent to ${trial.business_email}`);
      } else {
        toast.error(`Resend failed: ${r.data?.send_result?.reason || "unknown"}`);
      }
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "auth0_pwless_not_configured") {
        toast.error("Auth0 passwordless not configured.");
      } else {
        toast.error(typeof detail === "object" ? (detail.message || detail.error) : (detail || "Resend failed."));
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleExtend = async ({ days, reason }) => {
    if (!extending) return;
    setSubmitting(true);
    setBusyId(extending.id);
    try {
      const r = await api.post(`/admin/trials/${extending.id}/extend`, { days, reason });
      toast.success(`Extended by ${days} day${days === 1 ? "" : "s"} → ${fmtDate(r.data?.new_expires_at)}`);
      setExtending(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "object" ? (detail.message || detail.error) : (detail || "Extend failed."));
    } finally {
      setSubmitting(false);
      setBusyId(null);
    }
  };

  const totals = useMemo(() => data?.counts || {}, [data]);
  const grandTotal = useMemo(
    () => Object.values(totals).reduce((s, n) => s + (n || 0), 0),
    [totals],
  );

  if (loading && !data) {
    return (
      <div className="py-12 text-center ek-text-muted" data-testid="countdown-loading">
        Loading countdown…
      </div>
    );
  }

  return (
    <div data-testid="trial-countdown-panel">
      <ToolTrialsExpiring />

      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">Trial countdown</h2>
          <p className="text-[13px] ek-text-muted mt-1 max-w-2xl">
            Approved trials ordered by days remaining. Use “Resend link” for
            prospects who never accepted the magic-link, and “Extend” to add
            time without a re-approval cycle.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {grandTotal > 0 && (
            <div className="text-[11px] tracking-[0.14em] uppercase ek-text-muted" data-testid="countdown-grand-total">
              {grandTotal} approved trial{grandTotal === 1 ? "" : "s"}
            </div>
          )}
          <button
            type="button"
            onClick={load}
            className="ek-btn ek-btn-outline text-[12px] inline-flex items-center gap-2"
            data-testid="countdown-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {grandTotal === 0 ? (
        <div className="border ek-line px-6 py-12 text-center" data-testid="countdown-empty">
          <CheckCircle2 size={28} className="mx-auto ek-text-muted" />
          <div className="ek-heading text-base mt-3">No approved trials yet</div>
          <div className="text-[13px] ek-text-muted mt-1">
            Once you approve trial requests, they’ll appear here as live conversion candidates.
          </div>
        </div>
      ) : (
        <>
          <Bucket name="urgent" rows={data.buckets.urgent} onExtend={setExtending} onResend={handleResend} busyId={busyId} />
          <Bucket name="warm" rows={data.buckets.warm} onExtend={setExtending} onResend={handleResend} busyId={busyId} />
          <Bucket name="no_clock" rows={data.buckets.no_clock} onExtend={setExtending} onResend={handleResend} busyId={busyId} />
          <Bucket name="early" rows={data.buckets.early} onExtend={setExtending} onResend={handleResend} busyId={busyId} />
        </>
      )}

      {extending && (
        <ExtendModal
          trial={extending}
          submitting={submitting}
          onClose={() => setExtending(null)}
          onConfirm={handleExtend}
        />
      )}
    </div>
  );
}
